import React from 'react';
import styles from './OnboardingStepper.module.css';

interface Step {
  number: number;
  title: string;
}

interface OnboardingStepperProps {
  currentStep: number;
  steps: Step[];
  onStepClick?: (step: number) => void;
}

const getStepIcon = (stepNumber: number): React.ReactNode => {
  switch (stepNumber) {
    case 1:
      // Business Info - Store/Shop icon
      return (
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 4a1 1 0 011-1h10a1 1 0 011 1v2h2a1 1 0 110 2h-.5v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8H2.5a1 1 0 110-2H4V4zm3 0v2h6V4H7zm0 4v8h6V8H7z" />
        </svg>
      );
    case 2:
      // Contact Info - User/Person icon
      return (
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
        </svg>
      );
    case 3:
      // Documents - Briefcase icon
      return (
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0h8v2H6V4zm0 4h8v6H6V8z" />
        </svg>
      );
    case 4:
      // ACH Setup - Building/Bank icon
      return (
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2h2a1 1 0 110 2h-2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8H1a1 1 0 110-2h2V4zm2 2v8h10V6H5zm1 2h2v4H6V8zm4 0h2v4h-2V8z" />
        </svg>
      );
    case 5:
      // Signature - Checkmark/Check icon
      return (
        <svg viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      );
    default:
      return stepNumber;
  }
};

export const OnboardingStepper: React.FC<OnboardingStepperProps> = ({
  currentStep,
  steps,
  onStepClick,
}) => {
  return (
    <div className={styles.stepperWrapper}>
      <div className={styles.stepperTrack}>
        <ul className={styles.stepperList}>
          {steps.map((step, index) => {
            const isCompleted = step.number < currentStep;
            const isActive = step.number === currentStep;
            const isPending = step.number > currentStep;

            return (
              <li key={step.number} className={styles.stepItem}>
                {/* Connector line before step (except first step) */}
                {index > 0 && (
                  <div
                    className={`${styles.connector} ${
                      steps[index - 1].number < currentStep ? styles.connectorCompleted : ''
                    }`}
                  />
                )}

                {/* Step button */}
                <button
                  onClick={() => onStepClick?.(step.number)}
                  disabled={isPending}
                  className={`${styles.stepButton} ${
                    isCompleted ? styles.completed : isActive ? styles.active : styles.pending
                  }`}
                  type="button"
                  aria-label={step.title}
                >
                  {getStepIcon(step.number)}
                </button>

                {/* Step label */}
                <span className={styles.stepLabel}>{step.title}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
